/**
 * Fan — the air-current push zone (Brief 014). Super Meat Boy's fans: a region that
 * shoves the player a fixed direction while they're inside it (no contact-with-ground
 * requirement, unlike a CONVEYOR belt).
 *
 * A non-solid tile-aligned ZONE (`props.wCells × hCells`). Each overlap step it adds a
 * `power·dt` nudge along `props.dir` (4-way) and clamps the induced speed to `power` so
 * it can't accelerate without bound. Non-lethal by design (beatable-safe — a fan never
 * kills; pair it with a hazard if you want danger).
 *
 * Determinism: add + min/max clamp + an integer animation counter → ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_POWER = 900;

/** 4-way push unit vectors. Screen y is DOWN → "up" is negative y. */
const DIRS: Record<string, { ux: number; uy: number }> = {
  up:    { ux: 0,  uy: -1 },
  down:  { ux: 0,  uy: 1 },
  left:  { ux: -1, uy: 0 },
  right: { ux: 1,  uy: 0 },
};

export class Fan implements Entity {
  readonly type = EntityType.FAN;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private power: number;
  private dir: { ux: number; uy: number };
  private animTick = 0;

  /** @param props `wCells`/`hCells` (default 1×3), `dir` (4-way; default 'up'),
   *               `power` (px/s; default 900). Zone extends right + down from the cell. */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const w = posInt(props?.wCells, 1);
    const h = posInt(props?.hCells, 3);
    this.body = { x: cx - ts / 2, y: feetY - ts, w: w * ts, h: h * ts };
    const p = props?.power;
    this.power = typeof p === 'number' && p > 0 ? p : DEFAULT_POWER;
    const d = typeof props?.dir === 'string' ? props.dir : 'up';
    this.dir = DIRS[d] ?? DIRS.up;
  }

  update(_dt: number, _ctx: EntityContext): void { this.animTick++; }

  onPlayerOverlap(ctx: EntityContext): void {
    const p = ctx.player;
    const push = this.power * (1 / 60); // fixed-step nudge (dt is the sim's 1/60)
    if (this.dir.ux !== 0) {
      p.vx += this.dir.ux * push;
      if (this.dir.ux > 0) p.vx = Math.min(p.vx, this.power);
      else p.vx = Math.max(p.vx, -this.power);
    }
    if (this.dir.uy !== 0) {
      p.vy += this.dir.uy * push;
      if (this.dir.uy < 0) { p.vy = Math.max(p.vy, -this.power); p.grounded = false; }
      else p.vy = Math.min(p.vy, this.power);
    }
  }

  spriteKey(): string { return Math.floor(this.animTick / 4) % 2 === 0 ? 'fan' : 'fan_alt'; }

  visible(): boolean { return true; }

  renderSize(): { w: number; h: number } { return { w: this.body.w, h: this.body.h }; }
}

function posInt(v: unknown, dflt: number): number {
  return typeof v === 'number' && v >= 1 ? Math.floor(v) : dflt;
}
